/**
 * Uplifted from `apps/web/src/utils/OfflineUtil.js` — WEB half.
 *
 * Four DB verbs, each with the same three-way shape: if this device is a SLAVE with a configured
 * master, POST to the master; else if Electron, call the typed `window.ristaDB` IPC; else fall
 * back to the legacy `nativeHooks.exec` bridge.
 *
 * ⚠️ **One duplicate collapsed:** the app file declared its own
 * `const isElectron = () => !!window.electronAPI?.isElectron;`, byte-identical to
 * `nativeHooks`'s. Core imports the single definition instead ("one definition, always"). The
 * app file did not export it, so no surface changes.
 *
 * ⚠️ `window` -> `getWindow()`, read INSIDE each function (ledger hazard 18).
 * ⚠️ Deep-imports `../constants/index.desktop` and `../../data/constants/index.desktop`
 * (hazard 6/16): `LOCALSTORAGE_KEYS`-style tables and the NW-app event maps are web-only or
 * divergent, so a bare folder import would typecheck against `.native`.
 */
import { isEmpty } from "lodash";
import store from "../../redux-store";
import { getWindow } from "../../effects-bridge";
import { exec, isElectron } from "../nativeHooks";
import {
  NW_APP_EVENT_ACTIONS,
  NW_APP_EVENT_KEYS,
} from "../../data/constants";
import {
  getLSRequestPayloadData,
  isMaster,
} from "../../helpers/localServer";

export * from "./shared";

/**
 * Slave-device DB ops POST to the master via the LAN HTTP server. Under Electron this goes
 * through the typed `server.post` IPC (Promise -> `response.data` | `error`); under the legacy
 * shells it goes through `nativeHooks.exec` with a `callbackFn`.
 *
 * Private in the app file and private here — not exported.
 */
function postToLocalServer(d: any, params: any) {
  if (isElectron()) {
    getWindow()
      .electronAPI.server.post(d)
      .then((response: any) => {
        if (response?.error) {
          params.callbackFn?.(null, response.error);
        } else {
          params.callbackFn?.(response?.data, null);
        }
      })
      .catch((err: any) => params.callbackFn?.(null, err));
  } else {
    exec(NW_APP_EVENT_KEYS.server, NW_APP_EVENT_ACTIONS.server.post, d, params);
  }
}

/**
 * True when this device should route DB writes to a LAN master instead of its own store.
 *
 * ⚠️ **A deliberate EXTRACTION, not a verbatim move — the only structural change in this file.**
 * The app repeats `localServer && !isEmpty(localServer.deviceIdentifier) && !isMaster()` inline
 * in all four verbs. Verified before collapsing: 4 occurrences, **1 distinct line** on web, and
 * the same on mobile (`_.isEmpty`). Private and unexported, so no surface change; the probe
 * asserts all four verbs route identically for the same store state.
 */
function routesToMaster(localServer: any) {
  return localServer && !isEmpty(localServer.deviceIdentifier) && !isMaster();
}

export function del(params: any) {
  const tid = store.getState().businessStore?.businessData?.branchId;
  let posSettings = store.getState().catalogStore?.posSettings || {},
    localServer = posSettings.localServer;
  if (routesToMaster(localServer)) {
    let message = {
        domain: params.domain,
        tid: tid,
        type: "DB_DELETE",
        data: params.keys,
        filter: params.filter,
      },
      d = getLSRequestPayloadData("db", message);
    postToLocalServer(d, params);
  } else if (isElectron()) {
    getWindow()
      .ristaDB.delete(params.domain, tid, params.keys)
      .then((result: any) => params.callbackFn?.(result, null))
      .catch((err: any) => params.callbackFn?.(null, err));
  } else {
    exec(
      NW_APP_EVENT_KEYS.ristadb,
      NW_APP_EVENT_ACTIONS.ristadb.delete,
      [params.domain, tid, params.keys, params.callbacks],
      params,
    );
  }
}

export function replace(params: any) {
  const tid = store.getState().businessStore?.businessData?.branchId;
  let posSettings = store.getState().catalogStore?.posSettings || {},
    localServer = posSettings.localServer;
  if (routesToMaster(localServer)) {
    let message = {
        domain: params.domain,
        tid: tid,
        type: "DB_REPLACE",
        data: params.keys,
        filter: params.filter,
      },
      d = getLSRequestPayloadData("db", message);
    postToLocalServer(d, params);
  } else if (isElectron()) {
    getWindow()
      .ristaDB.replace(params.domain, tid, params.values)
      .then((result: any) => params.callbackFn?.(result, null))
      .catch((err: any) => params.callbackFn?.(null, err));
  } else {
    exec(
      NW_APP_EVENT_KEYS.ristadb,
      NW_APP_EVENT_ACTIONS.ristadb.replace,
      [params.domain, tid, params.values, params.callbacks],
      params,
    );
  }
}

/**
 * ⚠️ `get` is the only verb that MUTATES its argument: when `params.filter` is present it merges
 * `userData` + `businessData` into `params.filter.params` before dispatching. Callers rely on
 * that (the offline SALE filters run against the current user), so it is preserved verbatim,
 * including the mutation rather than a copy.
 */
export function get(params: any) {
  const busionessStore = store.getState().businessStore;
  const tid = busionessStore?.businessData?.branchId;
  let posSettings = store.getState().catalogStore?.posSettings || {},
    localServer = posSettings.localServer;

  if (params.filter) {
    params.filter.params = {
      ...params.filter.params,
      userData: busionessStore?.userData,
      businessData: busionessStore?.businessData,
    };
  }

  if (routesToMaster(localServer)) {
    let message = {
        domain: params.domain,
        tid: tid,
        type: "DB_GET",
        data: params.keys,
        filter: params.filter,
      },
      d = getLSRequestPayloadData("db", message);
    postToLocalServer(d, params);
  } else if (isElectron()) {
    getWindow()
      .ristaDB.get(params.domain, tid, params.keys, params.filter)
      .then((records: any) => params.callbackFn?.(records, null))
      .catch((err: any) => params.callbackFn?.(null, err));
  } else {
    exec(
      NW_APP_EVENT_KEYS.ristadb,
      NW_APP_EVENT_ACTIONS.ristadb.get,
      [params.domain, tid, params.keys, params.filter, params.callbacks],
      params,
    );
  }
}

/**
 * ⚠️ `put` differs from the other three in TWO ways, both load-bearing:
 *  · its LAN envelope sends `data: { key, value }` from `params.values` — not `params.keys`, and
 *    with no `filter`;
 *  · its Electron call takes a 4th argument, `isOffline`, read from
 *    `globalStore.isOfflineModeActive ?? false`, and its `.then` inspects `result?.error`
 *    instead of assuming success.
 */
export function put(params: any) {
  const tid = store.getState().businessStore?.businessData?.branchId;
  const isOffline = store.getState().globalStore?.isOfflineModeActive ?? false;
  let posSettings = store.getState().catalogStore?.posSettings || {},
    localServer = posSettings.localServer;
  if (routesToMaster(localServer)) {
    let message = {
        domain: params.domain,
        tid: tid,
        type: "DB_PUT",
        data: {
          key: params.values.key,
          value: params.values.value,
        },
      },
      d = getLSRequestPayloadData("db", message);
    postToLocalServer(d, params);
  } else if (isElectron()) {
    getWindow()
      .ristaDB.put(params.domain, tid, params.values, isOffline)
      .then((result: any) => {
        if (result?.error) {
          params.callbackFn?.(null, result.error);
        } else {
          params.callbackFn?.(result, null);
        }
      })
      .catch((err: any) => params.callbackFn?.(null, err));
  } else {
    exec(
      NW_APP_EVENT_KEYS.ristadb,
      NW_APP_EVENT_ACTIONS.ristadb.put,
      [params.domain, tid, params.values, params.callbacks],
      params,
    );
  }
}
